import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon } from "@heroicons/react/20/solid";
import { Modal } from "@components";
import { toast } from "@components/toast/Toast";
import { areFiltersEqual } from "@components/tables/shared";
import { useUiPreferences } from "@api";
import { cn, formatAmount } from "@utils";
import {
  DEFAULT_WORKING_SET_COLOUR,
  DEFAULT_WORKING_SET_ICON,
  WORKING_SET_NAME_MAX,
  WORKING_SET_SLOTS,
  firstFreeSlot,
  useFilterStats,
  useWorkingSetList,
} from "../hooks/workingSets";
import { WORKING_SET_TEMPLATES, completeFilters, newCondition, suggestName } from "../hooks/workingSetBuilder";
import { useBuilderFields } from "../hooks/useBuilderFields";
import { useWorkingSetActions } from "../hooks/useWorkingSetActions";
import WorkingSetConditions, { FieldRail } from "./WorkingSetConditions";
import WorkingSetPreview from "./WorkingSetPreview";
import WorkingSetIcon from "./WorkingSetIcon";
import { WorkingSetColourPicker, WorkingSetIconPicker } from "./WorkingSetIconPicker";
import RemoveWorkingSetDialog from "./RemoveWorkingSetDialog";

const SAVED_VIEW_TYPE = "view:tradeAccounts";

// The statistics endpoint takes at most 20 searches per call; templates use the first few.
const MAX_STATS_QUERIES = 20;

const parseView = (pref) => {
  try {
    const filters = JSON.parse(pref.value);
    return Array.isArray(filters) && filters.length > 0 ? { id: pref.id, name: pref.name, filters } : null;
  } catch {
    return null;
  }
};

// Only the persisted fields: chip components and option lists come from Trade Accounts' columns.
const toStoredFilter = ({ key, title, type, value }) => ({ key, title, type, value });

const statsLine = (stats) => {
  if (stats.isLoading) return <span className="inline-block h-3 w-16 animate-pulse rounded bg-zinc-100" />;
  if (stats.isError || stats.count == null) return null;
  if (stats.count === 0) return <span className="text-zinc-400">No accounts today</span>;
  return (
    <>
      <span className="font-semibold text-zinc-800">{stats.count}</span>
      <span className="text-zinc-500"> · {formatAmount(stats.overdue ?? 0)}</span>
    </>
  );
};

const TILE =
  "flex min-h-40 flex-col items-start gap-4 rounded-xl border p-5 text-left transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500";

const TemplateTile = ({ template, stats, onHome, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    className={cn(TILE, "border-zinc-200 bg-white hover:-translate-y-px hover:border-zinc-300 hover:shadow-md")}
  >
    <span className="flex w-full items-start justify-between gap-2">
      <WorkingSetIcon icon={template.icon} colour={template.colour} className="size-10" iconClassName="size-5" />
      {onHome && (
        <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">On your home</span>
      )}
    </span>
    <span className="flex flex-col gap-1">
      <span className="text-[15px] font-semibold text-zinc-900">{template.name}</span>
      <span className="text-sm text-zinc-500">{template.blurb}</span>
    </span>
    <span className="mt-auto text-sm tabular-nums">{statsLine(stats)}</span>
  </button>
);

const StartStep = ({ existing, savedViews, onPick, onScratch, onFullFilterBar }) => {
  const counted = savedViews.slice(0, MAX_STATS_QUERIES - WORKING_SET_TEMPLATES.length);
  const stats = useFilterStats([...WORKING_SET_TEMPLATES.map((t) => t.filters), ...counted.map((v) => v.filters)]);
  const viewStats = stats.slice(WORKING_SET_TEMPLATES.length);
  const isOnHome = (filters) => existing.some((s) => areFiltersEqual(s.filters, filters));

  return (
    <div className="flex flex-col gap-8 p-6">
      <section className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-4">
          <h4 className="text-[15px] font-semibold text-zinc-900">Start from a template</h4>
          <span className="text-sm text-zinc-400">Live counts from your accounts</span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <button
            type="button"
            onClick={onScratch}
            className={cn(TILE, "border-dashed border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50")}
          >
            <span className="flex size-10 items-center justify-center rounded-lg bg-white text-zinc-600 ring-1 ring-zinc-200">
              <PlusIcon aria-hidden="true" className="size-5" />
            </span>
            <span className="flex flex-col gap-1">
              <span className="text-[15px] font-semibold text-zinc-900">Start from scratch</span>
              <span className="text-sm text-zinc-500">Pick your own conditions</span>
            </span>
          </button>
          {WORKING_SET_TEMPLATES.map((t, i) => (
            <TemplateTile
              key={t.id}
              template={t}
              stats={stats[i]}
              onHome={isOnHome(t.filters)}
              onPick={() =>
                onPick({ filters: t.filters, name: t.name, icon: t.icon, colour: t.colour, source: t.name })
              }
            />
          ))}
        </div>
      </section>

      {savedViews.length > 0 && (
        <section className="flex flex-col gap-3">
          <h4 className="text-[15px] font-semibold text-zinc-900">Or a saved filter</h4>
          <div className="flex flex-wrap gap-2">
            {savedViews.map((v, i) => (
              <button
                key={v.id}
                type="button"
                onClick={() =>
                  onPick({
                    filters: v.filters,
                    name: v.name,
                    icon: DEFAULT_WORKING_SET_ICON,
                    colour: DEFAULT_WORKING_SET_COLOUR,
                    source: v.name,
                  })
                }
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-800 transition-colors hover:border-zinc-300 hover:text-zinc-900"
              >
                {v.name}
                {viewStats[i]?.count != null && (
                  <span className="text-xs font-normal tabular-nums text-zinc-400">{viewStats[i].count}</span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      <button
        type="button"
        onClick={onFullFilterBar}
        className="inline-flex items-center gap-1 self-start text-[13px] font-medium text-zinc-500 hover:text-zinc-900"
      >
        Need every filter? Build it on Trade accounts
        <ArrowRightIcon aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  );
};

const Label = ({ htmlFor, children }) =>
  htmlFor ? (
    <label htmlFor={htmlFor} className="text-sm text-zinc-600">
      {children}
    </label>
  ) : (
    <span className="text-sm text-zinc-600">{children}</span>
  );

const AppearancePanel = ({ filters, name, setName, suggestedName, icon, setIcon, colour, setColour, duplicateOf }) => (
  <div className="flex flex-col gap-6 p-6">
    <WorkingSetPreview
      filters={filters}
      name={name.trim() || suggestedName}
      icon={icon}
      colour={colour}
      duplicateOf={duplicateOf}
    />
    <section className="flex flex-col gap-4">
      <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">How it shows on Today's tasks</span>
      <div className="flex flex-col gap-2">
        <Label htmlFor="working-set-name">Name</Label>
        <input
          id="working-set-name"
          type="text"
          maxLength={WORKING_SET_NAME_MAX}
          value={name}
          placeholder={suggestedName || "Name this set"}
          onChange={(e) => setName(e.target.value)}
          className="h-10 rounded-lg border border-zinc-200 px-3 text-sm text-zinc-900 shadow-sm outline-none placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label>Colour</Label>
        <WorkingSetColourPicker value={colour} onChange={setColour} />
      </div>
      <div className="flex flex-col gap-2">
        <Label>Icon</Label>
        <WorkingSetIconPicker value={icon} onChange={setIcon} />
      </div>
    </section>
  </div>
);

/**
 * The one place working sets are defined, in three modes:
 * - create (default): pick a starting point, then build.
 * - create from filters (`initialFilters`): straight to build, seeded with Trade Accounts filters.
 * - edit (`set`): straight to build with the set loaded; saves in place, and can remove it.
 * A new set lands in `slot` on the Home board (else the first free one) and opens in Trade Accounts
 * unless `onCreated` takes over.
 */
const WorkingSetBuilderModal = ({ isOpen, onClose, set = null, initialFilters = null, slot = null, onCreated, onRemoved }) => {
  const navigate = useNavigate();
  const { data: preferences = [] } = useUiPreferences();
  const { sets: existing } = useWorkingSetList();
  const fields = useBuilderFields();
  const actions = useWorkingSetActions();
  const isEdit = !!set;
  const startsInBuild = isEdit || !!initialFilters;

  const [step, setStep] = useState("start");
  const [source, setSource] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [lastAddedKey, setLastAddedKey] = useState(null);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(DEFAULT_WORKING_SET_ICON);
  const [colour, setColour] = useState(DEFAULT_WORKING_SET_COLOUR);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(startsInBuild ? "build" : "start");
    setSource(null);
    setConditions((set?.filters ?? initialFilters ?? []).map((f) => ({ ...f })));
    setLastAddedKey(null);
    setName(set?.name ?? "");
    setIcon(set?.icon ?? DEFAULT_WORKING_SET_ICON);
    setColour(set?.colour ?? DEFAULT_WORKING_SET_COLOUR);
    setSaving(false);
    // Reset only when the modal opens, not when the set it was opened with refreshes underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const savedViews = useMemo(
    () => preferences.filter((p) => p.type === SAVED_VIEW_TYPE).map(parseView).filter(Boolean),
    [preferences],
  );
  const filters = useMemo(() => completeFilters(conditions).map(toStoredFilter), [conditions]);
  const suggestedName = useMemo(() => suggestName(conditions, fields), [conditions, fields]);
  const duplicateOf =
    filters.length > 0 ? existing.find((s) => s.id !== set?.id && areFiltersEqual(s.filters, filters)) : null;
  const finalName = name.trim() || suggestedName;
  const unchanged =
    isEdit &&
    finalName === set.name &&
    icon === set.icon &&
    colour === set.colour &&
    areFiltersEqual(filters, set.filters);
  const targetSlot = isEdit ? null : slot ?? firstFreeSlot(existing);
  const boardFull = !isEdit && targetSlot === null;
  const canSave = filters.length > 0 && !!finalName && !saving && !unchanged && !boardFull;

  const toggleField = (field) => {
    if (conditions.some((c) => c.key === field.key)) {
      setConditions(conditions.filter((c) => c.key !== field.key));
      return;
    }
    setConditions([...conditions, newCondition(field)]);
    setLastAddedKey(field.key);
  };

  const pick = ({ filters: picked, name: pickedName, icon: pickedIcon, colour: pickedColour, source: pickedSource }) => {
    setConditions(picked.map((f) => ({ ...f })));
    setName(pickedName);
    setIcon(pickedIcon);
    setColour(pickedColour);
    setSource(pickedSource);
    setStep("build");
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    if (isEdit) {
      const ok = await actions.update(set, { name: finalName, icon, colour, filters });
      setSaving(false);
      if (ok) {
        toast({ description: `Saved "${finalName}"` });
        onClose();
      }
      return;
    }
    const created = await actions.create({ name: finalName, icon, colour, filters, order: targetSlot });
    if (!created) {
      setSaving(false);
      return;
    }
    toast({ description: `"${finalName}" added to Today's tasks` });
    onClose();
    if (onCreated) onCreated(created);
    else navigate(`/accounts?set=${created.id}`);
  };

  const isBuild = step === "build";
  const showAppearance = filters.length > 0;
  const title = isEdit
    ? `Edit ${set.name}`
    : isBuild && source
      ? `New working set · from ${source}`
      : "New working set";
  const description = isEdit
    ? "Changes apply on Today's tasks and in Trade accounts."
    : isBuild
      ? "Define who belongs in it. The count on Today's tasks stays live as accounts come and go."
      : "A working set is a live list of accounts you come back to every day. Pick a starting point.";
  const hint = boardFull
    ? `Your board holds ${WORKING_SET_SLOTS} working sets. Remove one to add another.`
    : conditions.length === 0
      ? "Add at least one condition"
      : filters.length === 0
        ? "Finish the condition to see who matches"
        : unchanged
          ? "No changes yet"
          : `⌘ + Enter to ${isEdit ? "save" : "create"}`;

  return (
    <>
      <Modal
        isOpen={isOpen && !removing}
        onClose={onClose}
        size="xl"
        fullHeight
        bodyClassName="flex flex-col overflow-hidden"
        title={title}
        description={description}
      >
        {isBuild ? (
          <form
            id="new-working-set"
            className={cn(
              "grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:overflow-hidden",
              showAppearance
                ? "md:grid-cols-[260px_minmax(0,1fr)_340px]"
                : "md:grid-cols-[260px_minmax(0,1fr)]",
            )}
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                save();
              }
            }}
          >
            <div className="border-b border-zinc-200 md:min-h-0 md:overflow-y-auto md:border-b-0 md:border-r">
              <FieldRail fields={fields} conditions={conditions} onToggle={toggleField} />
            </div>
            <section className="flex flex-col gap-4 bg-zinc-50/60 p-6 md:min-h-0 md:overflow-y-auto">
              <h4 className="text-[15px] font-semibold text-zinc-900">Accounts where…</h4>
              <WorkingSetConditions
                fields={fields}
                conditions={conditions}
                onChange={setConditions}
                autoFocusKey={lastAddedKey}
              />
            </section>
            {showAppearance && (
              <div className="border-t border-zinc-200 md:min-h-0 md:overflow-y-auto md:border-l md:border-t-0">
                <AppearancePanel
                  filters={filters}
                  name={name}
                  setName={setName}
                  suggestedName={suggestedName}
                  icon={icon}
                  setIcon={setIcon}
                  colour={colour}
                  setColour={setColour}
                  duplicateOf={duplicateOf}
                />
              </div>
            )}
          </form>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <StartStep
              existing={existing}
              savedViews={savedViews}
              onPick={pick}
              onScratch={() => {
                setSource(null);
                setConditions([]);
                setName("");
                setIcon(DEFAULT_WORKING_SET_ICON);
                setColour(DEFAULT_WORKING_SET_COLOUR);
                setStep("build");
              }}
              onFullFilterBar={() => {
                onClose();
                navigate("/accounts?newWorkingSet=1");
              }}
            />
          </div>
        )}

        {isBuild && (
          <div className="flex shrink-0 items-center gap-3 border-t border-zinc-200 px-6 py-4">
            {isEdit ? (
              <button
                type="button"
                onClick={() => setRemoving(true)}
                className="rounded-lg px-2 py-1.5 text-[13px] font-medium text-red-600 hover:bg-red-50"
              >
                Remove working set
              </button>
            ) : (
              !startsInBuild && (
                <button
                  type="button"
                  onClick={() => setStep("start")}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                >
                  <ArrowLeftIcon aria-hidden="true" className="size-4" />
                  Back
                </button>
              )
            )}
            <span className="ml-auto hidden text-sm text-zinc-400 sm:inline">{hint}</span>
            <button
              type="submit"
              form="new-working-set"
              disabled={!canSave}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400 disabled:shadow-none",
              )}
            >
              {saving ? <ClipLoader size={14} color="#fff" aria-hidden="true" /> : null}
              {isEdit ? "Save changes" : onCreated ? "Create working set" : "Create and open in Trade accounts"}
            </button>
          </div>
        )}
      </Modal>
      <RemoveWorkingSetDialog
        set={removing ? set : null}
        onClose={() => setRemoving(false)}
        onRemoved={() => {
          onClose();
          onRemoved?.();
        }}
      />
    </>
  );
};

export default WorkingSetBuilderModal;
